import 'url-search-params-polyfill';

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { ModalProvider } from 'react-modal-hook';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';

import oldApi from './api';
import { api } from './data/api';
import { sessionActions } from './entities/auth/session/actions';
import { ExperimentProvider } from './experiment';
import { Main } from './main';
import { initialiseFullStory, initialiseGoogleTagManager } from './plugins';
import { createHistory } from './store/history';
import { IState } from './store/reducer';
import createStore from './store/store';
import useSessionCookies from './view/hooks/useSessionCookies';

const root = createRoot(document.getElementById('root')!);

// this is only here for unit testing the init sequence
export const reactRender = (element: React.ReactNode) => root.render(element);

export const init = async () => {
	const history = createHistory('client');
	const store = createStore({} as IState, api, oldApi, history);

	// Try to get initial user from cookies
	const { readCookies, refreshSession, clearCookies } = useSessionCookies();
	const cookieSession = readCookies();

	if (cookieSession) {
		await refreshSession(cookieSession);

		// We're now back using localStorage, so clear the cookies
		clearCookies();
	}

	// try to get initial user from localStorage
	try {
		const user = await oldApi.authentication.getCurrentUser();
		const session = user ? user.getSignInUserSession() : null;

		if (user && session) {
			const [attributes, groups] = await Promise.all([
				oldApi.authentication.getUserAttributes(user),
				oldApi.authentication.getUserGroups(user),
			]);
			store.dispatch(
				sessionActions.SESSION_CREATE_RECEIVE({
					accessToken: session.getAccessToken().getJwtToken(),
					idToken: session.getIdToken().getJwtToken(),
					attributes,
					groups,
				})
			);
		}
	} catch (e) {
		// no need to redirect here, withAuthentication HOC takes care
		// store.dispatch(push('/'));
	}

	try {
		initialiseGoogleTagManager();
		initialiseFullStory();
	} catch (e) {
		// shouldn't stop working if GTM fails
	}

	const render = () => {
		reactRender(
			<Provider store={store}>
				<Router>
					<ModalProvider>
						<ExperimentProvider>
							<Main />
						</ExperimentProvider>
					</ModalProvider>
				</Router>
			</Provider>
		);
	};

	render();
};

if (process.env.NODE_ENV !== 'test') {
	init();
}
